import { ExternalLink, Newspaper } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import logoCzechCrunch from '@/assets/logo-czech-crunch.svg';
import logoE15 from '@/assets/logo-e15.svg';
import logoReporter from '@/assets/logo-reporter.svg';
import logoKondice from '@/assets/logo-kondice.png';
import logoZenaAZivot from '@/assets/logo-zena-a-zivot.png';
import logoExclusiveLife from '@/assets/logo-exclusive-life.png';

interface Article {
  media: string;
  logo: string;
  title: string;
  url?: string;
  printRef?: string;
}

const articles: Article[] = [
  {
    media: 'Czech Crunch',
    logo: logoCzechCrunch,
    title: 'Přeplavala Gibraltar a na kontě má přes 15 tisíc kilometrů. Teď spustila první českou plaveckou aplikaci',
    url: 'https://cc.cz/preplavala-gibraltar-a-na-konte-ma-pres-15-tisic-kilometru-ted-spustila-prvni-ceskou-plaveckou-aplikaci/',
  },
  {
    media: 'e15',
    logo: logoE15,
    title: 'Obeplavala Alcatraz, nyní za milion z rodinného rozpočtu spustila první českou aplikaci pro plavce',
    url: 'https://www.e15.cz/byznys/technologie-a-media/obeplavala-alcatraz-nyni-za-milion-z-rodinneho-rozpoctu-spustila-prvni-ceskou-aplikaci-pro-plavce-1429984',
  },
  {
    media: 'Reportér',
    logo: logoReporter,
    title: 'Běžný člověk by v tak chladné vodě do hodiny zemřel, říká Češka, která obeplula Alcatraz',
    url: 'https://reportermagazin.cz/101502/prvni-ceska-ktera-obeplula-alcatraz-bezny-clovek-by-v-tak-chladne-vode-do-hodiny-zemrel-rika/',
  },
  {
    media: 'Kondice',
    logo: logoKondice,
    title: 'V pohybu: Appka pro plavce',
    printRef: 'Magazín Kondice, únor 2026, str. 66',
  },
  {
    media: 'Žena a život',
    logo: logoZenaAZivot,
    title: 'Trenér do kapsy',
    printRef: 'Žena a život, č. 24, 19. 11. 2025, str. 82',
  },
  {
    media: 'Exclusive Life',
    logo: logoExclusiveLife,
    title: 'Swimmie – plav a zlepšuj se',
    printRef: 'Exclusive Life, zima 2025, ročník 7, str. 20',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

function ArticleCard({ article }: { article: Article }) {
  const content = (
    <div className="h-full p-6 rounded-2xl bg-white/95 backdrop-blur-sm shadow-md shadow-black/10 border border-white/50 hover:shadow-xl hover:shadow-black/15 hover:bg-white transition-all duration-500 hover:-translate-y-2 group">
      {/* Media logo */}
      <div className="h-10 mb-4 flex items-center">
        <img src={article.logo} alt={article.media} className="h-full max-w-[140px] object-contain" />
      </div>

      <h3 className="font-semibold text-base mb-3 text-foreground group-hover:text-primary transition-colors duration-300 leading-snug">
        {article.title}
      </h3>

      {article.url ? (
        <div className="flex items-center text-primary text-sm font-medium opacity-60 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
          <ExternalLink className="h-4 w-4 mr-1.5" />
          <span>Číst článek</span>
        </div>
      ) : (
        <div className="flex items-center text-foreground/50 text-sm">
          <Newspaper className="h-4 w-4 mr-1.5" />
          <span>{article.printRef}</span>
        </div>
      )}
    </div>
  );

  if (article.url) {
    return (
      <a href={article.url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return content;
}

export default function Press() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section className="py-24 md:py-36 px-4 bg-primary relative overflow-hidden" ref={ref}>
      {/* Top wave — from white Story section */}
      <div className="wave-divider-top">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,64L48,69.3C96,75,192,85,288,90.7C384,96,480,96,576,85.3C672,75,768,53,864,48C960,43,1056,53,1152,64C1248,75,1344,85,1392,90.7L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="white"
          />
        </svg>
      </div>

      {/* Bottom wave — into bg-muted Features section */}
      <div className="wave-divider">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="rgb(245, 245, 245)"
          />
        </svg>
      </div>

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/0 via-primary to-primary/0 pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-block text-white/80 font-semibold text-sm uppercase tracking-wider mb-4">
            Média
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            Napsali o nás
          </h2>
        </motion.div>

        {/* Cards grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {articles.map((article, idx) => (
            <motion.div key={idx} variants={cardVariants}>
              <ArticleCard article={article} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
